import React from 'react';
import { ArrowDownIcon, ArrowLeftRightIcon, ArrowRightIcon } from 'lucide-react';
import { IMAGES } from '../data/site';
import { QuoteForm } from './QuoteForm';
export type HeroVariant = 'van' | 'storefront';
const HERO_ALT = 'Concept image of two Kosin’s Glass service vans at a commercial building, one with its cargo door open';
const STOREFRONT_ALT = 'The Kosin’s Glass brick storefront with its tall freestanding red sign and the company’s van and service trucks parked in front';

/* Keep the stacked tablet layout shaded over the sky. */
const VAN_DESKTOP_SCRIM: React.CSSProperties = {
  backgroundImage: 'linear-gradient(180deg, rgba(0,0,0,0.66) 0%, rgba(0,0,0,0.64) 58%, rgba(0,0,0,0.48) 78%, rgba(0,0,0,0) 100%)'
};

/* Centered desktop copy needs shading across the sky and white vehicles. */
const VAN_CENTERED_DESKTOP_SCRIM: React.CSSProperties = {
  backgroundImage: 'linear-gradient(90deg, rgba(0,0,0,0.70) 0%, rgba(0,0,0,0.64) 50%, rgba(0,0,0,0.40) 66%, rgba(0,0,0,0) 82%)'
};

/* Mobile/tablet shading for the default van photo: covers the longest
   two-line CTA at 320px and fades out above the vehicle logos. */
const VAN_MOBILE_SCRIM: React.CSSProperties = {
  backgroundImage: 'linear-gradient(180deg, rgba(0,0,0,0.67) 0%, rgba(0,0,0,0.67) 84%, rgba(0,0,0,0) 100%)'
};

/* Neutral black scrim for the brighter storefront photo: strongest behind the
   copy, easing off smoothly over the lower vehicle and road area. */
const STOREFRONT_SCRIM: React.CSSProperties = {
  backgroundImage: 'linear-gradient(180deg, rgba(0,0,0,0.76) 0%, rgba(0,0,0,0.74) 40%, rgba(0,0,0,0.60) 62%, rgba(0,0,0,0.42) 82%, rgba(0,0,0,0.30) 100%), linear-gradient(90deg, rgba(0,0,0,0.22) 0%, rgba(0,0,0,0.08) 45%, rgba(0,0,0,0) 75%)'
};
const PHOTO_CLASS = 'fixed inset-x-0 top-0 h-[100lvh] w-full sm:absolute sm:h-[720px] lg:h-[620px] xl:h-full';
function buildCompareHref(variant: HeroVariant): string {
  if (typeof window === 'undefined') {
    return variant === 'storefront' ? '/' : '/?hero=storefront';
  }
  const params = new URLSearchParams(window.location.search);
  if (variant === 'storefront') {
    params.delete('hero');
  } else {
    params.set('hero', 'storefront');
  }
  const query = params.toString();
  return `${window.location.pathname}${query ? `?${query}` : ''}`;
}
interface HeroProps {
  variant?: HeroVariant;
}
export function Hero({
  variant = 'van'
}: HeroProps) {
  const isStorefront = variant === 'storefront';
  const compareHref = buildCompareHref(variant);
  const compareLabel = isStorefront ? 'View Van Photo Version' : 'View Storefront Photo Version';
  return <section aria-labelledby="hero-heading" className="relative isolate w-full bg-kosin-charcoal max-sm:[clip-path:inset(0)] sm:bg-kosin-mist xl:bg-kosin-charcoal" data-id="element-189" data-mp="111erd3dyn0k6">
      {isStorefront ? <img src={IMAGES.heroStorefront} alt={STOREFRONT_ALT} width={1672} height={941} className={`${PHOTO_CLASS} object-cover object-center [filter:saturate(0.72)]`} data-id="element-190" data-mp="3lui8konbuvkb" /> : <picture data-id="element-191" data-mp="21awd27ubmpfw">
          <source media="(min-width: 1024px)" srcSet={IMAGES.heroWide} width={2172} height={724} data-id="element-192" data-mp="21aekbl6vatcq" />
          <img src={IMAGES.heroMobile} alt={HERO_ALT} width={1086} height={1448} className={`${PHOTO_CLASS} object-cover object-bottom lg:object-left-bottom`} data-id="element-193" data-mp="267whyfffpm5f" />
        </picture>}

      <div aria-hidden="true" className="pointer-events-none fixed inset-0 bg-black/65 sm:hidden" data-id="element-194" data-mp="2kxubzc01p62j" />

      {isStorefront ? <div aria-hidden="true" className={`${PHOTO_CLASS} hidden sm:block`} style={STOREFRONT_SCRIM} data-id="element-195" data-mp="2oynzw5dhmkvf" /> : <>
          <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 hidden h-[440px] w-full sm:block sm:h-[360px] lg:hidden" style={VAN_MOBILE_SCRIM} data-id="element-196" data-mp="umzhft81s06a" />
          <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 hidden h-[360px] lg:block xl:hidden" style={VAN_DESKTOP_SCRIM} data-id="element-197" data-mp="4aclrbd6jn6w" />
          <div aria-hidden="true" className="pointer-events-none absolute inset-0 hidden xl:block" style={VAN_CENTERED_DESKTOP_SCRIM} data-id="element-198" data-mp="e8ilpnffej94" />
        </>}

      <div className="relative mx-auto flex max-w-shell flex-col px-[18px] pb-6 sm:block sm:px-6 sm:pb-0 xl:grid xl:min-h-[620px] xl:grid-cols-[minmax(0,1fr)_400px] xl:items-start xl:gap-9 xl:py-6" data-id="element-199" data-mp="3hrbvrq8zwpyj">
        <div className="contents pt-6 sm:block sm:min-h-[720px] sm:pt-8 lg:min-h-[620px] xl:min-h-0 xl:self-center xl:pt-0" data-id="element-200" data-mp="3rh56kp1bd11q">
          <p className={`flex items-center gap-3 pt-6 text-[16px] font-bold sm:pt-0 xl:text-[18px] ${isStorefront ? 'text-white' : 'text-kosin-ondark'}`} data-id="element-201" data-mp="3mo2bgcgw1jjf">
            <span className="h-[3px] w-8 shrink-0 bg-kosin-ondark" aria-hidden="true" data-id="element-202" data-mp="2tylnbr0awot0" />
            Kosin’s Glass
          </p>

          <h1 id="hero-heading" className={`mt-2 max-w-[700px] text-[30px] font-bold leading-[1.1] text-white sm:text-[38px] xl:max-w-[740px] xl:text-[52px] xl:leading-[1.08] ${isStorefront ? '[text-shadow:0_1px_6px_rgba(0,0,0,0.45)]' : '[text-shadow:0_2px_4px_rgba(0,0,0,0.9)]'}`} data-id="element-203" data-mp="1eaf9kd8fayqa">
            Glass Repair, Replacement &amp; Custom Glass
          </h1>

          <p className="mt-3 max-w-[560px] max-sm:order-2 max-sm:mt-5 text-[16px] leading-[1.5] text-white sm:text-[17px] xl:max-w-[660px] xl:text-[20px] [text-shadow:0_1px_12px_rgba(0,0,0,0.6)]" data-id="element-204" data-mp="1j8gvr715be5z">
            Family-owned since 2000, serving Southeast Michigan’s vehicles,
            homes and businesses.
            <span className="hidden xl:inline" data-id="element-205" data-mp="bun7rdicogfs">
              {' '}From windshield replacement to custom shower doors and storefronts,
              we help you choose the right glass and handle the installation.
            </span>
          </p>

          <p className="mt-3 text-[14px] leading-5 max-sm:order-2 text-white sm:text-[15px] xl:text-[17px] xl:leading-7 [text-shadow:0_1px_12px_rgba(0,0,0,0.6)]" data-id="element-206" data-mp="29jscfl0elgsm">
            <span className="block font-semibold sm:inline" data-id="element-207" data-mp="1zt7hc7jxuueg">Three Convenient Locations</span>
            <span className="mx-2 hidden text-white/60 sm:inline" aria-hidden="true" data-id="element-208" data-mp="1vqt1lwbkgare">
              |
            </span>
            Howell • Ann Arbor • Novi
          </p>

          <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2 text-[14px] font-semibold text-white max-sm:order-2 xl:text-[16px]" data-id="element-209" data-mp="1qucbwxopxoc7">
            <a href="#hero-quote" className="focus-light inline-flex min-h-8 items-center gap-2 underline underline-offset-4 xl:hidden" data-id="element-210" data-mp="1guboazjtgm27">
              Get A Free Quote
              <ArrowDownIcon className="h-4 w-4" aria-hidden="true" data-id="element-211" data-mp="3b7l3n7z1n1sd" />
            </a>
            <a href="#services" className="focus-light inline-flex min-h-8 items-center gap-2 underline underline-offset-4 xl:min-h-11" data-id="element-212" data-mp="3jpd9mlxiagny">
              Explore Our Services
              <ArrowRightIcon className="h-4 w-4 xl:h-5 xl:w-5" aria-hidden="true" data-id="element-213" data-mp="3sgvpevqjiugi" />
            </a>
          </div>
        </div>

        <div id="hero-quote" className="pt-5 max-sm:order-1 sm:pb-8 xl:w-[400px] xl:justify-self-end xl:py-0" data-id="element-214" data-mp="1achboc39ilw">
          <QuoteForm idPrefix="hero" data-id="element-215" data-mp="30rsmpkeaxs5z" />
        </div>

        <a href={compareHref} className="focus-light relative z-10 mt-6 self-start max-sm:order-3 sm:absolute sm:mt-0 sm:self-auto inline-flex items-center gap-2 rounded bg-black/60 px-3 py-1.5 text-[13px] font-semibold text-white underline underline-offset-4 hover:bg-black/75 transition-colors duration-150 ease-kosin sm:left-6 sm:top-[664px] lg:top-[564px] xl:bottom-5 xl:top-auto" data-id="element-216" data-mp="34qszoma3m0l2">
          <ArrowLeftRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-217" data-mp="tygka239zhkw" />
          {compareLabel}
        </a>
      </div>
    </section>;
}